import { useCallback } from 'react';

import { useProperty } from 'src/app/Property.store';

/**
 * Per-property capability gating.
 *
 * The backend derives `role` + `rights` per caller and returns them on
 * `GET property/{id}`. The FE branches on those backend-provided values and
 * NEVER computes rights itself — exactly mirroring the pools `viewerRole`
 * pattern (`src/api/pools/index.ts`). Write endpoints are re-authorized
 * server-side, so this layer is cosmetic UX, not security.
 *
 * Rollout policy — fail open until deployed:
 *  - `rights` absent/undefined (backend hasn't shipped the field) → allow all
 *    (legacy access), so a not-yet-deployed backend doesn't black out the app.
 *  - `rights` present as an array → strict allow-list; `[]` denies everything.
 *  `Array.isArray(rights)` is the exact "backend is authoritative" discriminator.
 */

// Frozen contract — must match backend GET property/{id} `rights[]` exactly.
export const CAPABILITIES = [
  'view_reports',
  'view_billing',
  'view_pools',
  'create_ticket',
  'submit_authority_form',
  'end_agreement',
  'manage_container_reminders',
  'manage_pool',
  'send_message',
  'edit_comms_preferences',
  'manage_reports',
  'download_invoice',
  'pay_invoice',
  'purchase_product',
  'submit_feedback',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export type Capabilities = {
  /** Backend-derived per-property role (for display, e.g. RoleBadge). */
  role: string | undefined;
  rights: string[] | undefined;
  /** True once the backend deployed the field (rights is an array). */
  gated: boolean;
  can: (right: Capability) => boolean;
};

export function useCapabilities(): Capabilities {
  const { data } = useProperty();
  const rights = data?.rights;

  const can = useCallback(
    (right: Capability): boolean => {
      // Not deployed (undefined/null) → fail open to legacy access.
      if (!Array.isArray(rights)) return true;
      // Deployed → strict allow-list. An empty array denies everything.
      return rights.includes(right);
    },
    [rights]
  );

  return { role: data?.role, rights, gated: Array.isArray(rights), can };
}

export function useCan(right: Capability): boolean {
  return useCapabilities().can(right);
}
